// src/stores/ContentDataStore.js
// =============================================================
// v12 step 2 — 多環境 JSON 來源切換
//
// 三種模式自動切換:
//   1. localhost / 127.0.0.1 → 本機 demo (public/seven-straits.json)
//   2. 預覽網域 (PREVIEW_HOST) → Apps Script Web App 即時讀 GD
//   3. 正式網域 → GCS 上的正式 JSON
//
// 維護:當預覽 / 正式網域固定後,只要改下面三個常數即可。
// =============================================================
import { writable } from 'svelte/store'

// ⭐ 預覽網域 — Cloudflare Pages / Netlify 的 host 字串(不含 https://)
//   留空字串時不會被觸發,等部署到正式預覽環境後再填。
//   範例:'seven-straits-preview.pages.dev'
const PREVIEW_HOST = 'seven-straits-preview.pages.dev'

// ⭐ Apps Script Web App URL — 部署成 web app 後拿到的 .../exec 結尾網址
//   範例:'https://script.google.com/macros/s/AKfyc.../exec'
const APPS_SCRIPT_WEB_APP_URL = 'https://script.google.com/macros/s/AKfycbytYBc0ZpKj6OYvBQcEFSRUzDa6Ls9riFWy35Ma6qyxX79eCULJzKlrrrEJE2zNe0A2/exec'

// ⭐ 正式環境 JSON URL(GCS / 其他 CDN)
const PRODUCTION_JSON_URL = 'https://datastore.thenewslens.com/infographic/eight-straits-2026/seven-straits.json'

// ⭐ 本機 / fallback JSON URL
const LOCAL_JSON_URL = '/seven-straits.json'

// =============================================================
// 全形標點正規化保險絲 (v12.1)
// -------------------------------------------------------------
// 為什麼要做這個:
//   ArchieML 原文用全形標點 (，。？！：；),但有些工具 / 編輯流程
//   會在解析時把全形 ， 轉成半形 , (例如 pandoc / extract-text)。
//   結果就會出現「逗號半形、句號頓號全形」的混排,排版上很跳。
//
// 規則設計原則:
//   1. 只處理「中文/中文標點 旁邊的」半形標點 → 全形,避免動到:
//      - URL (https://...)
//      - 英文片語裡的標點 (Photo Credit:, Captain Phillips)
//      - 數字之間的逗號 (1,234)
//   2. 失敗就 fallback 回原字串 (safe by default)
//   3. 只跑一次,結果就是給 Svelte 看的最終資料
//
// 改 ArchieML / 換編輯流程的話這層可以不動 — 它只是保險。
// =============================================================
// 中文表意文字 + CJK 兼容區
const CJK = '[\\u3400-\\u9fff\\uF900-\\uFAFF]'
// 中文標點
const CJK_PUNCT = '[，。、！？「」『』：；（）—…《》〈〉·]'
const CJK_CTX = `(?:${CJK}|${CJK_PUNCT})`

const RE_COMMA  = new RegExp(`(${CJK_CTX}),(?=${CJK_CTX})`, 'g')
const RE_QMARK  = new RegExp(`(${CJK_CTX})\\?`, 'g')
const RE_COLON  = new RegExp(`(${CJK})\\:(?=${CJK})`, 'g')
const RE_SEMI   = new RegExp(`(${CJK})\\;`, 'g')
const RE_EXCLAM = new RegExp(`(${CJK})\\!`, 'g')

function normalizePunct(str) {
  if (typeof str !== 'string') return str
  try {
    return str
      .replace(RE_COMMA,  '$1，')
      .replace(RE_QMARK,  '$1？')
      .replace(RE_COLON,  '$1：')
      .replace(RE_SEMI,   '$1；')
      .replace(RE_EXCLAM, '$1！')
  } catch (e) {
    return str
  }
}

// 遞迴跑整個物件
function normalizePunctDeep(node) {
  if (node == null) return node
  if (typeof node === 'string') return normalizePunct(node)
  if (Array.isArray(node)) return node.map(normalizePunctDeep)
  if (typeof node === 'object') {
    const out = {}
    for (const k of Object.keys(node)) out[k] = normalizePunctDeep(node[k])
    return out
  }
  return node
}

// =============================================================
// Markdown 連結 → HTML <a> 轉換 (v12.2)
// -------------------------------------------------------------
// 為什麼要做這個:
//   ArchieML 內文是直接用 Markdown 語法寫連結:[文字](URL)
//   但 ArchieML parser 不會把 Markdown 轉成 HTML,所以前端
//   {@html value} 拿到的還是原始 Markdown 純文字,看不到連結。
//
// 規則:
//   1. 配對 [文字](URL),轉成 <a href="URL" target="_blank"
//      rel="noopener noreferrer">文字</a>
//   2. URL 限定 http / https 開頭,避免誤抓到「[1](註1)」這種
//      不是真連結的寫法。
//   3. 已知是「整欄都是 URL 字串」的 key (url / article_url /
//      article_img_url / landSource / coord / id 等) 整個跳過,
//      避免把單純的 URL 欄位變成 <a> HTML。
// =============================================================

// Markdown 連結配對:[文字](http(s)://...)
//   - 文字組:不能含有 ]
//   - URL  組:不能含有 ) 或空白
const RE_MD_LINK = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g

function escapeHtmlAttr(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function linkifyMarkdown(str) {
  if (typeof str !== 'string') return str
  if (str.indexOf('](') === -1) return str   // 早退:沒有可能的 markdown link
  try {
    return str.replace(RE_MD_LINK, (_match, text, url) => {
      const safeUrl = escapeHtmlAttr(url)
      return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`
    })
  } catch (e) {
    return str
  }
}

// 這幾個 key 對應的 value 我們知道是「整段就是 URL 字串」,
// 不要對它們跑 markdown 轉換(否則 url: [http://x](http://x)
// 這種 Word 自動連結化的寫法會被轉成 <a> HTML,塞進 <img src=> 會壞掉)。
const URL_FIELD_KEYS = new Set([
  'url',
  'article_url',
  'article_img_url',
  'landSource',
])

function linkifyMarkdownDeep(node, parentKey = null) {
  if (node == null) return node
  if (typeof node === 'string') {
    if (parentKey && URL_FIELD_KEYS.has(parentKey)) {
      // URL 欄位:把 Word 自動加上的 [url](url) 形式還原成純 URL
      // (取第一個 capture group 就是原始 URL)
      return node.replace(RE_MD_LINK, (_m, _txt, url) => url)
    }
    return linkifyMarkdown(node)
  }
  if (Array.isArray(node)) return node.map(item => linkifyMarkdownDeep(item, parentKey))
  if (typeof node === 'object') {
    const out = {}
    for (const k of Object.keys(node)) out[k] = linkifyMarkdownDeep(node[k], k)
    return out
  }
  return node
}

function resolveContentUrl() {
  // SSR 或非瀏覽器環境(rare,但保險):走 local
  if (typeof window === 'undefined') return LOCAL_JSON_URL

  const host = window.location.hostname

  // 1. 本機開發
  if (host === 'localhost' || host === '127.0.0.1' || host === '' /* file:// */) {
    return LOCAL_JSON_URL
  }

  // 2. 預覽環境 → Apps Script Web App
  if (PREVIEW_HOST && host === PREVIEW_HOST && APPS_SCRIPT_WEB_APP_URL) {
    return APPS_SCRIPT_WEB_APP_URL
  }

  // 3. 預設:正式環境
  return PRODUCTION_JSON_URL
}

// cache buster:每次重整都重抓最新 JSON
const cacheBuster = `?_=${Date.now()}`
const contentDataUrl = resolveContentUrl() + cacheBuster

const ContentDataStore = writable(undefined, async set => {
  try {
    const res = await fetch(contentDataUrl)
    if (!res.ok) {
      console.error('[ContentDataStore] fetch failed:', res.status, contentDataUrl)
      return
    }
    const data = await res.json()
    // ⭐ v12.1 跑一次全形標點正規化 (中文之間的半形 , ? : ; ! → 全形)
    const normalized = normalizePunctDeep(data)
    // ⭐ v12.2 Markdown 連結 [text](url) → <a> HTML
    const linkified = linkifyMarkdownDeep(normalized)
    set(linkified)
    if (linkified.__preview_meta) {
      console.info('[ContentDataStore] preview build, generated at:', linkified.__preview_meta.generated_at)
    }
  } catch (err) {
    console.error('[ContentDataStore] error:', err, '\nURL:', contentDataUrl)
  }
  return () => {}
})

export default ContentDataStore
